import * as LDClient from "launchdarkly-js-client-sdk";
import { useEffect } from "react";
import { useAppSelector } from "src/store/hooks";
import { getDefaultLogLevel, setLogLevel } from "src/services/logger";

export class LaunchDarklyService {
  private static instance: LaunchDarklyService;
  public static client: LDClient.LDClient | null = null;

  private constructor() {}

  static getInstance(): LaunchDarklyService {
    if (!LaunchDarklyService.instance) {
      LaunchDarklyService.instance = new LaunchDarklyService();
    }
    return LaunchDarklyService.instance;
  }

  static getClient(): LDClient.LDClient | null {
    return LaunchDarklyService.client;
  }

  async init(user: LDClient.LDSingleKindContext) {
    if (LaunchDarklyService.client) return LaunchDarklyService.client;

    LaunchDarklyService.client = LDClient.initialize(import.meta.env.VITE_LD_CLIENT_ID, user, {
      bootstrap: "localStorage",
      streaming: true,
    });

    await LaunchDarklyService.client.waitForInitialization(5);

    return LaunchDarklyService.client;
  }

  static async isFlagEnabled(flagKey: string, defaultValue = false): Promise<boolean> {
    try {
      if (!LaunchDarklyService.client) {
        throw new Error("LaunchDarkly not initialized");
      }

      const ld = LaunchDarklyService.client;
      return ld.variation(`sfweb_${flagKey}`, defaultValue);
    } catch {
      return defaultValue;
    }
  }

  static variation<T>(flagKey: string, defaultValue: T): T {
    try {
      if (!LaunchDarklyService.client) {
        throw new Error("LaunchDarkly not initialized");
      }

      return LaunchDarklyService.client.variation(`sfweb_${flagKey}`, defaultValue);
    } catch {
      return defaultValue;
    }
  }

  /**
   * Aplica o nível de log vindo da flag `sfweb_log_level` e reaplica em
   * tempo real quando a flag muda (streaming habilitado).
   */
  static applyLogLevelFlag() {
    const client = LaunchDarklyService.getClient();
    if (!client) return;

    const apply = () => setLogLevel(client.variation("sfweb_log_level", getDefaultLogLevel()));

    apply();

    client.on("change:sfweb_log_level", apply);
  }
}

export function LaunchDarklyBootstrap() {
  const email = useAppSelector((state) => state.auth.userInfo?.email);

  useEffect(() => {
    if (!email) return;

    async function bootstrap() {
      const ldService = LaunchDarklyService.getInstance();
      await ldService.init({
        kind: "user",
        key: email,
      });
    }

    bootstrap();
  }, [email]);

  return null;
}
